import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  FileSpreadsheet,
  Users,
  Link2,
  Terminal,
  Layers,
  Shield,
  Target,
  Send,
  CheckCircle2,
  X,
  Mail,
  Phone,
  Building2,
  Zap,
} from "lucide-react";
import { useInView, useCountUp } from "@/hooks/useAnimateOnView";

/**
 * "How it works — 5 steps" landing section — animated variant.
 *
 * Motion pattern (mirrors bettercontact.rocks/#how-it-works):
 *   • Each step card fades + slides up as it scrolls into view.
 *   • A single vertical SVG spine to the left of the cards draws itself
 *     downward, linked to the scroll position of the section.
 *   • A short horizontal "plug-in" line draws from the spine into each card
 *     the moment the card enters the viewport.
 *   • Rows inside the waterfall + validator demos stagger in one-by-one.
 *   • Numeric counters (leads-sorted, valid, elapsed) tick up on entry.
 *
 * Everything is scroll-linked or IntersectionObserver-driven — no libs.
 */
export default function HowItWorks({ primaryHref = "/register" }) {
  const containerRef = useRef(null);
  const [spineProgress, setSpineProgress] = useState(0); // 0..1 fill of vertical line

  // Scroll-linked spine drawing (based on how much of the section is visible)
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    let raf = null;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      // Progress = how far the section has scrolled through the viewport.
      // 0 when top of section is at the bottom of viewport,
      // 1 when the bottom of section reaches the top of viewport.
      const total = rect.height + vh;
      const scrolled = vh - rect.top;
      const p = Math.max(0, Math.min(1, scrolled / total));
      setSpineProgress(p);
      raf = null;
    };
    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative overflow-hidden py-24 sm:py-32 bg-[#fafaf7]"
      data-testid="landing-how-it-works"
    >
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* header — scroll-animated */}
        <HeaderReveal />

        {/* steps — the spine SVG lives ABSOLUTELY behind the cards */}
        <div className="mt-16 sm:mt-24 relative">
          {/* Scroll-linked drawing spine (SVG for pixel-perfect stroke fill) */}
          <Spine progress={spineProgress} />

          <StepCard
            index="01"
            badge="IN"
            status="IMPORT"
            title="Pipe leads in from anywhere."
          >
            <p>
              CSV upload with dedupe, a single manual contact, a LinkedIn URL,
              or a REST API call. Need to enrich just an email or a mobile? Use{" "}
              <span className="font-semibold text-ink-900">Find Email</span>{" "}
              and{" "}
              <span className="font-semibold text-ink-900">Find Mobile</span> —
              type a name and company, get the contact.
            </p>
            <InputDemo />
          </StepCard>

          <StepCard
            index="02"
            badge="AI"
            status="ENRICHING"
            title="LeadIQ AI routes and cascades."
          >
            <p>
              Every source has its unique strengths. Our router analyses the
              lead — domain, geography, seniority — and builds the optimal
              provider sequence, then cascades through vetted sources. One
              waterfall, both work emails <em>and</em> mobile numbers.
            </p>
            <WaterfallDemo />
          </StepCard>

          <StepCard
            index="03"
            badge="4L"
            status="VALIDATION"
            title="Every result passes a 4-layer validator."
          >
            <p>
              Syntax → MX → SMTP → catch-all detection for email. Carrier +
              HLR + live-ping for mobile. Fail a layer and we skip the result
              and move to the next provider — you're{" "}
              <span className="font-semibold text-ink-900">
                never charged for invalid data.
              </span>
            </p>
            <ValidatorDemo />
          </StepCard>

          <StepCard
            index="04"
            badge="FS"
            status="OPTIONAL"
            title="Personal ICP fit + intent scoring."
          >
            <p>
              Every enriched lead is scored 0–100 against <em>your</em>{" "}
              offering — not a generic industry benchmark — and sorted into P1
              / P2 / P3 tiers based on fit + live intent (hiring, funding,
              tech-stack match). Open the P1 list, dial less, close more.
            </p>
            <TierBucketDemo />
          </StepCard>

          <StepCard
            index="05"
            badge="EX"
            status="EXPORT"
            title="Ship enriched data anywhere."
            last
          >
            <p>
              Send via our built-in AI Outreach from your own Gmail (BYO
              inbox, replies stay yours), download the CSV, or push to your
              stack. HubSpot, Salesforce, Pipedrive, Instantly, and Smartlead
              one-way sync is on the roadmap — vote inside the app.
            </p>
            <ActivateDemo />
          </StepCard>
        </div>

        {/* closing line */}
        <div className="mt-16 sm:mt-24 text-center">
          <div className="text-xs uppercase tracking-widestx font-mono text-ink-500">
            Built, enriched, validated, scored — and{" "}
            <span className="text-brand font-semibold">
              you only pay for verified hits.
            </span>
          </div>
        </div>

        {/* closing CTA */}
        <div className="mt-16 rounded-sm border border-ink-100 bg-white p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
              45 seconds to first enrichment
            </div>
            <h3 className="mt-3 font-display font-black text-2xl sm:text-4xl tracking-tighter leading-tight text-ink-900">
              The list you gave up on?
              <br />
              <span className="italic">Run it through LeadIQ.</span>
            </h3>
            <p className="mt-3 text-sm text-ink-500 max-w-lg">
              30-day full-access trial · No credit card · Only pay for
              verified hits.
            </p>
          </div>
          <Link
            to={primaryHref}
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-6 py-3.5 rounded-sm text-sm transition-colors shrink-0"
            data-testid="how-it-works-final-cta"
          >
            Start free trial
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* keyframes */}
      <style>{`
        @keyframes hiw-fade-slide-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hiw-check-in {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes hiw-pulse {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 1; }
        }
        @keyframes hiw-highlight-flash {
          0% { box-shadow: 0 0 0 0 rgba(0, 47, 167, 0); }
          40% { box-shadow: 0 0 0 5px rgba(0, 47, 167, 0.18); }
          100% { box-shadow: 0 0 0 0 rgba(0, 47, 167, 0); }
        }
        /* Base state for staggered items — invisible until parent gets .hiw-visible.
           visibility:hidden prevents the "ghost outline" during CSS animation delay. */
        .hiw-stagger-item {
          opacity: 0;
          visibility: hidden;
        }
        .hiw-visible .hiw-stagger-item {
          animation: hiw-fade-slide-in 0.7s both cubic-bezier(0.22, 1, 0.36, 1);
          visibility: visible;
        }
        .hiw-visible .hiw-stagger-item:nth-child(1) { animation-delay: 0.15s; }
        .hiw-visible .hiw-stagger-item:nth-child(2) { animation-delay: 0.30s; }
        .hiw-visible .hiw-stagger-item:nth-child(3) { animation-delay: 0.45s; }
        .hiw-visible .hiw-stagger-item:nth-child(4) { animation-delay: 0.60s; }
        .hiw-visible .hiw-stagger-item:nth-child(5) { animation-delay: 0.75s; }
        .hiw-visible .hiw-stagger-item:nth-child(6) { animation-delay: 0.90s; }
        .hiw-visible .hiw-stagger-item:nth-child(7) { animation-delay: 1.05s; }
        .hiw-visible .hiw-stagger-item:nth-child(8) { animation-delay: 1.20s; }
      `}</style>
    </section>
  );
}

/* ─────────────────── Header reveal (scroll-animated) ─────────────────── */
function HeaderReveal() {
  const [ref, visible] = useInView({ threshold: 0.2 });
  const revealStyle = (delay) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
  });
  return (
    <div ref={ref}>
      <div
        className="flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-400"
        style={revealStyle(0)}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
        How it works
      </div>
      <h2
        className="mt-4 font-display font-black text-4xl sm:text-6xl tracking-tighter leading-[1.02] text-ink-900"
        style={revealStyle(0.08)}
      >
        Five steps from zero
        <br />
        <span className="italic font-black">to a booked meeting.</span>
      </h2>
      <p
        className="mt-6 text-base text-ink-500 leading-relaxed max-w-xl"
        style={revealStyle(0.18)}
      >
        Bring a CSV, a single contact, or a name. LeadIQ Pro finds the emails
        and mobile numbers, verifies them, scores fit against <em>your</em>{" "}
        ICP, and hands the whole package back — ready to outreach.
      </p>
    </div>
  );
}


/* ─────────────────── Vertical drawing spine ─────────────────── */
function Spine({ progress }) {
  // The spine sits behind the cards on the left. It's a single SVG line
  // whose stroke-dashoffset shrinks toward zero as the user scrolls the
  // section through the viewport.
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute left-4 sm:left-6 top-0 bottom-0 w-1.5 hidden md:block"
    >
      {/* dim baseline (undrawn track) */}
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-ink-100" />
      {/* filled portion — height scales with scroll progress */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-0 w-[3px] rounded-full bg-gradient-to-b from-brand via-brand to-brand/30 origin-top will-change-[height]"
        style={{
          height: `${(progress * 100).toFixed(1)}%`,
          transition: "height 180ms cubic-bezier(0.22, 1, 0.36, 1)",
          boxShadow: "0 0 12px rgba(0, 47, 167, 0.25)",
        }}
      />
      {/* traveling dot at the tip */}
      <div
        className="absolute left-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-brand ring-2 ring-brand/20"
        style={{
          top: `calc(${(progress * 100).toFixed(1)}% - 7px)`,
          opacity: progress > 0.02 && progress < 0.99 ? 1 : 0,
          transition: "opacity 0.3s ease, top 180ms cubic-bezier(0.22, 1, 0.36, 1)",
          boxShadow: "0 0 16px rgba(0, 47, 167, 0.55)",
        }}
      />
    </div>
  );
}

/* ─────────────────── Step card shell ─────────────────── */
function StepCard({ index, badge, status, title, children, last }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className="relative md:pl-14">
      {/* short horizontal plug-in line connecting spine → card */}
      <div
        aria-hidden
        className="hidden md:block absolute left-6 top-10 h-[2px] w-8 bg-brand origin-left rounded-full"
        style={{
          transform: visible ? "scaleX(1)" : "scaleX(0)",
          transition:
            "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.25s",
          boxShadow: "0 0 8px rgba(0, 47, 167, 0.35)",
        }}
      />

      <div
        className={`relative rounded-sm border border-ink-100 bg-white p-6 sm:p-8 shadow-sm ${
          visible ? "hiw-visible" : ""
        }`}
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(28px)",
          transition:
            "opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1), transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {/* header row */}
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono text-ink-400 tabular-nums">
            {index}
          </span>
          <span className="inline-flex items-center justify-center w-8 h-6 rounded-sm bg-brand text-white text-[10px] font-mono font-bold">
            {badge}
          </span>
          <h3 className="flex-1 font-display font-bold text-lg sm:text-xl text-ink-900 tracking-tight leading-tight">
            {title}
          </h3>
          <span className="hidden sm:inline-flex items-center gap-1 rounded-sm border border-ink-100 bg-ink-50 px-2 py-1 text-[10px] uppercase tracking-widestx font-mono text-ink-500">
            {status}
          </span>
        </div>
        {/* body + demo */}
        <div className="mt-4 space-y-5 text-sm text-ink-500 leading-relaxed max-w-3xl">
          {children}
        </div>
      </div>
      {!last && <div className="h-14" />}
    </div>
  );
}

/* ─────────────────── 01 · IN — Input methods ─────────────────── */
function InputDemo() {
  const chips = [
    { icon: FileSpreadsheet, label: "CSV upload", note: "Drag & drop" },
    { icon: Users, label: "Manual entry", note: "Paste one" },
    { icon: Link2, label: "LinkedIn URL", note: "Any profile" },
    { icon: Mail, label: "Find Email", note: "Name + company" },
    { icon: Phone, label: "Find Mobile", note: "Name + company" },
    { icon: Terminal, label: "REST API", note: "Webhooks" },
  ];
  return (
    <div className="rounded-sm border border-ink-100 bg-ink-50/40 p-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {chips.map((c) => (
          <div
            key={c.label}
            className="hiw-stagger-item rounded-sm border border-ink-100 bg-white px-3 py-2.5 flex items-center gap-2.5"
          >
            <c.icon className="w-3.5 h-3.5 text-ink-500 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-ink-900 truncate leading-tight">
                {c.label}
              </div>
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 leading-tight">
                {c.note}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── 02 · AI — Waterfall (with counters) ─────────────────── */
function WaterfallDemo() {
  const [ref, visible] = useInView({ threshold: 0.4 });
  const tried = useCountUp(4, { active: visible, duration: 900, delay: 500 });
  const valid = useCountUp(1, { active: visible, duration: 900, delay: 500 });
  const elapsed = useCountUp(4, { active: visible, duration: 900, delay: 500 }); // 0.4 s ×10
  const providers = [
    { name: "Source A", state: "queued" },
    {
      name: "Source B",
      state: "hit",
      conf: "0.94",
      note: "work email + mobile",
    },
    { name: "Source C", state: "skip" },
    { name: "Source D", state: "queued" },
  ];
  return (
    <div
      ref={ref}
      className={`rounded-sm border border-ink-100 bg-ink-50/40 p-4 ${
        visible ? "hiw-visible" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-500">
          <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
          LeadIQ router · re-ranking 14 sources
        </div>
        <div className="text-[10px] font-mono text-ink-500 tabular-nums">
          Tried <span className="text-ink-900 font-semibold">{tried}</span>/4 ·
          Valid{" "}
          <span className="text-emerald-700 font-semibold">{valid}</span> ·{" "}
          <span className="text-ink-900 font-semibold">{(elapsed / 10).toFixed(1)}s</span>
        </div>
      </div>
      <div className="text-xs text-ink-500 mb-3">
        Enriching{" "}
        <span className="font-mono text-ink-900">alex.wiegel @ amazon.com</span>
      </div>
      <div className="space-y-1.5">
        {providers.map((p, i) => (
          <div
            key={p.name}
            className="hiw-stagger-item flex items-center gap-3 rounded-sm border border-ink-100 bg-white px-3 py-2.5"
          >
            <div className="text-[10px] font-mono text-ink-400 w-6 shrink-0">
              P{i + 1}
            </div>
            <div className="flex-1 text-xs font-semibold text-ink-900">
              {p.name}
            </div>
            {p.state === "hit" && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-xs text-ink-500">{p.note}</span>
                <span className="text-emerald-700 font-mono text-xs">
                  {p.conf}
                </span>
                <span
                  className="inline-flex items-center gap-1 rounded-sm bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] text-emerald-800 font-semibold uppercase"
                  style={{ animation: "hiw-check-in 0.4s 0.5s both" }}
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Verified
                </span>
              </div>
            )}
            {p.state === "skip" && (
              <span className="inline-flex items-center gap-1 rounded-sm bg-ink-50 border border-ink-100 px-1.5 py-0.5 text-[10px] text-ink-500 font-mono uppercase">
                <X className="w-3 h-3" />
                Skipped
              </span>
            )}
            {p.state === "queued" && (
              <span
                className="inline-flex items-center gap-1 rounded-sm bg-ink-50 border border-ink-100 px-1.5 py-0.5 text-[10px] text-ink-500 font-mono uppercase"
                style={{ animation: "hiw-pulse 1.6s infinite" }}
              >
                Queued
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 text-[11px] text-ink-500">
        <Layers className="w-3 h-3 text-ink-400" />
        Re-ranked using{" "}
        <span className="text-ink-900 font-semibold">545k+</span> historical
        hits.
      </div>
    </div>
  );
}

/* ─────────────────── 03 · 4L — Validator ─────────────────── */
function ValidatorDemo() {
  const [ref, visible] = useInView({ threshold: 0.4 });
  const rows = [
    { label: "Syntax", value: "Pass" },
    { label: "MX record", value: "amazon-smtp.amazon.com" },
    { label: "SMTP handshake", value: "250 OK" },
    { label: "Catch-all", value: "Not detected" },
    { label: "Role mailbox", value: "No · alex.* is personal" },
  ];
  return (
    <div
      ref={ref}
      className={`rounded-sm border border-ink-100 bg-ink-50/40 p-4 ${
        visible ? "hiw-visible" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-500">
          <Shield className="w-3 h-3" />
          LeadIQ validator · reasoning
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </div>
        <span
          className="inline-flex items-center gap-1 rounded-sm bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] text-emerald-800 font-semibold uppercase"
          style={{
            animation: visible ? "hiw-check-in 0.4s 1.3s both" : "none",
          }}
        >
          <CheckCircle2 className="w-3 h-3" />
          Valid · 0.94
        </span>
      </div>
      <div className="text-xs text-ink-500 mb-3">
        Validating{" "}
        <span className="font-mono text-ink-900">alex.wiegel@amazon.com</span>
      </div>
      <div className="rounded-sm border border-ink-100 bg-white p-3 space-y-1.5">
        {rows.map((r) => (
          <div
            key={r.label}
            className="hiw-stagger-item flex items-center gap-3 text-xs"
          >
            <div className="text-ink-400 font-mono w-28 shrink-0 uppercase tracking-wider text-[10px]">
              {r.label}
            </div>
            <div className="flex-1 text-ink-900 font-medium truncate">
              {r.value}
            </div>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          </div>
        ))}
      </div>
      <div
        className="mt-3 rounded-sm bg-ink-900 text-white/90 p-3 text-xs leading-relaxed"
        style={{
          animation: visible ? "hiw-fade-slide-in 0.6s 1.1s both" : "none",
        }}
      >
        <span className="text-brand-300 font-mono text-[10px] uppercase tracking-widestx">
          Verdict
        </span>
        <div className="mt-1 text-white">
          Deliverable. Amazon SMTP accepted the handshake and the mailbox is
          not a catch-all — safe to send.
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── 04 · FS — Tier buckets (P1/P2/P3) ─────────────────── */
function TierBucketDemo() {
  const [ref, visible] = useInView({ threshold: 0.4 });
  const sorted = useCountUp(3214, { active: visible, duration: 1500, delay: 500 });
  const buckets = [
    {
      label: "P3 · Bad fit",
      note: "Wrong industry, wrong size, no signals.",
      badge: "Drop",
      badgeCls: "bg-ink-100 text-ink-500 border-ink-200",
      rowCls: "border-ink-100 bg-white",
      pct: "0–39",
    },
    {
      label: "P2 · Warm fit",
      note: "Right ICP but no active signals right now.",
      badge: "Nurture",
      badgeCls: "bg-amber-50 text-amber-800 border-amber-200",
      rowCls: "border-ink-100 bg-white",
      pct: "40–74",
    },
    {
      label: "P1 · Hot fit",
      note: "Perfect ICP + live buying signal firing now.",
      badge: "Reach out",
      badgeCls: "bg-brand text-white border-brand",
      rowCls: "border-brand/40 bg-brand/5 ring-1 ring-brand/20",
      pct: "75–100",
      hot: true,
    },
  ];
  return (
    <div
      ref={ref}
      className={`rounded-sm border border-ink-100 bg-ink-50/40 p-4 ${
        visible ? "hiw-visible" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-500">
          <Target className="w-3 h-3" />
          Fit-score bucketing
        </div>
        <div className="text-[10px] font-mono text-ink-500 tabular-nums">
          <span className="text-ink-900 font-semibold">
            {sorted.toLocaleString()}
          </span>{" "}
          leads sorted
        </div>
      </div>
      <div className="space-y-2">
        {buckets.map((b) => (
          <div
            key={b.label}
            className={`hiw-stagger-item flex items-center gap-3 rounded-sm border px-3 py-3 ${b.rowCls}`}
            style={
              b.hot && visible
                ? { animation: "hiw-fade-slide-in 0.55s 0.65s both ease-out, hiw-highlight-flash 1.2s 1.2s ease-out" }
                : undefined
            }
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink-900 truncate">
                {b.label}
              </div>
              <div className="text-[11px] text-ink-500 truncate">{b.note}</div>
            </div>
            <div className="text-[10px] font-mono text-ink-400 tabular-nums">
              {b.pct}
            </div>
            <span
              className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[10px] uppercase tracking-widestx font-mono font-semibold ${b.badgeCls}`}
            >
              {b.badge}
            </span>
          </div>
        ))}
      </div>
      <div
        className="mt-3 rounded-sm bg-white border border-ink-100 p-3 flex flex-wrap gap-2"
        style={{
          animation: visible ? "hiw-fade-slide-in 0.6s 1.3s both" : "none",
        }}
      >
        <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mr-1 self-center">
          Live intent on P1:
        </span>
        <IntentChip icon={Users} label="Hiring 3 Sr. AEs" tone="emerald" />
        <IntentChip icon={Zap} label="Raised $34M · 2 wk ago" tone="amber" />
        <IntentChip icon={Building2} label="Uses HubSpot · match" tone="sky" />
      </div>
    </div>
  );
}

function IntentChip({ icon: Icon, label, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
  };
  return (
    <div
      className={`flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[11px] font-medium ${tones[tone]}`}
    >
      <Icon className="w-3 h-3 shrink-0" />
      {label}
    </div>
  );
}

/* ─────────────────── 05 · EX — Activate ─────────────────── */
function ActivateDemo() {
  const [ref, visible] = useInView({ threshold: 0.4 });
  const destinations = [
    { label: "AI Outreach", note: "Send from your Gmail", live: true },
    { label: "CSV export", note: "One-click download", live: true },
    { label: "REST API", note: "Webhooks + polls", live: true },
    { label: "HubSpot", note: "Push contacts", live: false },
    { label: "Salesforce", note: "Push leads", live: false },
    { label: "Instantly", note: "Sync sequence", live: false },
    { label: "Smartlead", note: "Sync sequence", live: false },
    { label: "Pipedrive", note: "Push deals + contacts", live: false },
  ];
  return (
    <div
      ref={ref}
      className={`rounded-sm border border-ink-100 bg-ink-50/40 p-4 ${
        visible ? "hiw-visible" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-500">
          <Send className="w-3 h-3" />
          Destinations
        </div>
        <div className="text-[10px] font-mono text-ink-500">
          <span className="text-emerald-700 font-semibold">3 live</span> · 5
          roadmap
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {destinations.map((d, i) => (
          <div
            key={d.label}
            className="hiw-stagger-item rounded-sm border border-ink-100 bg-white p-3"
            style={
              visible && d.live
                ? {
                    animation: `hiw-fade-slide-in 0.5s ${(0.05 + i * 0.10).toFixed(2)}s both ease-out, hiw-highlight-flash 1s ${(0.55 + i * 0.10).toFixed(2)}s ease-out`,
                  }
                : undefined
            }
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs font-semibold text-ink-900 leading-tight">
                {d.label}
              </div>
              {d.live ? (
                <span className="text-[9px] font-mono uppercase tracking-widestx bg-emerald-500 text-white px-1.5 py-0.5 rounded-sm">
                  Live
                </span>
              ) : (
                <span className="text-[9px] font-mono uppercase tracking-widestx bg-ink-50 text-ink-500 border border-ink-100 px-1.5 py-0.5 rounded-sm">
                  Soon
                </span>
              )}
            </div>
            <div className="text-[10px] mt-1 leading-tight text-ink-500">
              {d.note}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
